import { useActionContext, useInsightsV2Store, useTransactionStore } from '../../../common'
import type { Beat, Transaction, DrilldownPayload } from '../../../core'
import type { TransactionDetailsBlockData } from '../../../core/types/InsightsInstanceSlotData'
import type { BottomActions, TopActions } from '../../../transactions/constants/Actions'
import { getBottomActionsByVariant } from './util'
import { resolveTopActions } from './transactionDetailsActions'

interface TransactionDetailsBlockState {
  bottomActions: BottomActions[]
  /** True while the store is still loading; the block shows a loader instead of an empty state. */
  isLoading: boolean
  topActions: TopActions[]
  /** `undefined` once loading finishes means the transaction could not be resolved. */
  transaction?: Transaction
}

/**
 * Resolves which transaction this drawer is about, and which inline actions it offers.
 *
 * Resolution order is deliberate:
 *  1. `instance_slot_data.transaction_guid` — the beat explicitly naming its subject.
 *  2. `beat.primary_transaction_guid` — the standard beat-level pointer.
 *  3. `beat.transaction_guids[0]` — last resort for beats that only carry the list.
 *
 * The guid is then looked up in `transactionStore.detailedTransactions` rather than used directly,
 * because `TransactionDetails` needs the *detailed* shape (account + category + taggings joined in)
 * and edits must write through to the same store object the rest of the app renders. `buildDetailed
 * Transactions` inside the store is this repo's equivalent of pulse's `buildTransactionDetails`.
 *
 * `beat.primary_transaction` is the fallback of last resort: it is a payload snapshot, not a store
 * object, so inline edits against it would not propagate. Preferring it over a store hit would make
 * editing silently no-op, which is why it is only used when the store genuinely has nothing — and
 * only when it is a snapshot of the *same* transaction the guid resolved to (or when no guid
 * resolved at all). A snapshot of some other transaction is not an answer to this question.
 */
export const useTransactionDetailsBlock = (beat: Beat): TransactionDetailsBlockState => {
  const { detailedTransactions, isTransactionDataLoaded } = useTransactionStore()

  const {actionDrawer} = useInsightsV2Store()

  const { transaction_guid } = useActionContext<DrilldownPayload>() ?? {}
  const slotData = (beat.insights_payload?.instance_slot_data ?? {}) as TransactionDetailsBlockData

  const guid = transaction_guid ??
    slotData.transaction_guid ?? beat.primary_transaction_guid ?? beat.transaction_guids?.[0]

  const fromStore = guid
    ? detailedTransactions.find((transaction) => transaction.guid === guid)
    : undefined

  // The snapshot is only a valid answer for the guid we actually resolved. When a beat names its
  // subject via instance_slot_data (or transaction_guids[0]) and that guid misses in the store,
  // falling back unconditionally would render a *different* transaction's details — and inline
  // edits would then be made against the wrong subject. Match on guid, or accept the snapshot only
  // when no guid resolved at all.
  const snapshot = beat.primary_transaction
  const snapshotMatches = !guid || snapshot?.guid === guid

  const bottomActions = getBottomActionsByVariant(actionDrawer, beat.insights_payload?.primary_cta, beat.insights_payload?.secondary_cta)
  return {
    bottomActions,
    isLoading: !isTransactionDataLoaded,
    topActions: resolveTopActions(slotData.top_actions),
    transaction: fromStore ?? (snapshotMatches ? snapshot : undefined),
  }
}
